// The science behind the screen, in the person's language, with the sources it rests on. Every
// URL here came from research/voice-of-the-rep-2026-09-05.md (verified in that brief); nothing is
// cited from memory. Numbers in the text are the brief's, not ours.

export interface Citation {
  n: number
  title: string
  who: string
  url: string
}

export const citations: Citation[] = [
  { n: 1, title: 'Learning to Classify Email into "Speech Acts"', who: 'Cohen, Carvalho & Mitchell, EMNLP 2004', url: 'https://aclanthology.org/W04-3240.pdf' },
  { n: 2, title: 'Dimensions of variation across Internet registers', who: 'Berber Sardinha, IJCL 2018', url: 'https://www.jbe-platform.com/content/journals/10.1075/ijcl.15026.ber' },
  { n: 3, title: 'Same Author or Just Same Topic?', who: 'Wegmann, Schraagen & Nguyen, 2022', url: 'https://aclanthology.org/2022.repl4nlp-1.26.pdf' },
  { n: 4, title: 'StyleDistance', who: 'Patel et al., NAACL 2025', url: 'https://arxiv.org/html/2410.12757v2' },
  { n: 5, title: 'Learning Universal Authorship Representations', who: 'Rivera-Soto et al., EMNLP 2021', url: 'https://aclanthology.org/2021.emnlp-main.70.pdf' },
  { n: 6, title: 'Catch Me If You Can? Not Yet', who: 'Findings of EMNLP 2025', url: 'https://arxiv.org/html/2509.14543v1' },
  { n: 7, title: 'Steering LLMs with Register Analysis', who: 'Yang & Carpuat, 2025', url: 'https://arxiv.org/html/2505.00679v2' },
  { n: 8, title: 'Individual Turing Test', who: 'March 2026', url: 'https://arxiv.org/html/2603.01289' },
  { n: 9, title: 'A Survey of Modern Authorship Attribution Methods', who: 'Stamatatos, JASIST 2009', url: 'https://icsdweb.aegean.gr/stamatatos/papers/survey.pdf' },
  { n: 10, title: 'Determining if Two Documents are by the Same Author', who: 'Koppel & Winter, JASIST 2014', url: 'https://u.cs.biu.ac.il/~koppel/papers/impostors-journal-revised2-140213.pdf' },
  { n: 11, title: 'Audience design', who: 'Bell 1984, summarised', url: 'https://en.wikipedia.org/wiki/Audience_design' },
  { n: 12, title: 'Evaluating Style-Personalized Text Generation', who: 'Jangra et al., 2026', url: 'https://arxiv.org/html/2508.06374' },
]

export interface Section {
  title: string
  body: string[]
  cites: number[]
}

export const sections: Section[] = [
  {
    title: 'Only your own words',
    body: [
      'Scout learns from what you typed, never from what you quoted. Before a message is kept, the quoted history, forwarded headers, disclaimers, and the signature block beneath your closing are stripped. Your closing line stays, because "Best," is part of your voice; your title and phone number are not.',
      'Only mail sent from your address counts. A reply where you wrote two words is dropped: there is nothing in it to learn.',
    ],
    cites: [9],
  },
  {
    title: 'Scenarios, not one blended voice',
    body: [
      'You do not write to a colleague the way you write to a client, and you do not thank someone the way you ask them for numbers. Linguists call the first effect audience design; the second is the speech act the message performs.',
      'Every message is sorted by channel, audience (colleagues share your domain; everyone else is a client or contact), and intent: asking, proposing, promising, delivering, thanking, reconnecting, introducing, seasonal notes, updates. The intent classifier keys on common-word patterns like "could you", "I\'ll", and "thanks for", which is what the original email speech-act work found discriminating, not topic words.',
    ],
    cites: [1, 11],
  },
  {
    title: 'Measured, not described',
    body: [
      'The radar and the numbers are counted from your mail: six register dimensions modelled on the multi-dimensional analysis of written registers, plus how you open, how you close, how long you run, how often you contract, and which marks you reach for.',
      'Structured dimensions steer a model far better than adjectives do. That is why the profile is a set of measurements a model is told to match, and why a description that is only adjectives ("professional, clear, friendly") is rejected outright.',
    ],
    cites: [2, 7],
  },
  {
    title: 'A style space of your own',
    body: [
      'Each message is turned into a style vector: hundreds of counts of function words and character patterns, which carry authorship, plus the habit statistics above. The vectors are stamped with the model that made them, so a profile is never compared across spaces.',
      'Style embeddings are built to be blind to topic. "Same author, different topic" must sit closer than "different author, same topic"; that is the test the platform library passes before any of this ships. A learned style model can replace the current one behind the same seam.',
    ],
    cites: [3, 4, 5],
  },
  {
    title: 'Examples chosen for range, not similarity',
    body: [
      'The examples on this page were picked to be as different from one another as possible while still being you, with a preference for recent mail. The measured finding is that examples chosen for topical similarity often make imitation worse, and that about five stylistically diverse ones capture most of the gain.',
    ],
    cites: [6],
  },
  {
    title: 'A gate before a draft',
    body: [
      'Every draft is scored against your profile before you see it: how close it sits to your centre, how close to your nearest real message, and whether any register dimension has drifted. The score is relative to how tightly your own messages cluster, so a terse writer and a discursive one are each held to their own spread. A draft that fails is rewritten with the failing dimensions named, up to three times.',
      'What you finally send, compared with the draft, is kept as an edit distance. That number is the honest measure of the whole feature, and it is the signal the next revision learns from.',
    ],
    cites: [12],
  },
  {
    title: 'What this cannot promise',
    body: [
      'Closeness in a style space is not the same as fooling someone who knows you. Recent work asking acquaintances to pick the real message from an imitation found they still can, more often than chance. Scout shows the verifier score, the examples, and the edit distance so that the claim on this page is always the measured one.',
    ],
    cites: [8, 10],
  },
]
